import { useCallback, useEffect, useRef } from 'react';
import { MiniKit, Permission } from '@worldcoin/minikit-js';

// Simple notification sound using Web Audio API (no external file needed)
function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    osc.type = 'sine';
    gain.gain.value = 0.1;
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
  } catch { /* audio not supported */ }
}

function vibrateDevice() {
  try {
    if ('vibrate' in navigator) navigator.vibrate([100, 50, 100]);
  } catch { /* not supported */ }
}

export function useNotifications() {
  const permissionRef = useRef<boolean>(false);

  useEffect(() => {
    if (MiniKit.isInstalled()) {
      // In World App: check existing permission state without requesting
      MiniKit.commandsAsync.getPermissions().then((result: any) => {
        if (result?.permissions?.notifications) {
          permissionRef.current = true;
        }
      }).catch(() => {});
    } else if ('Notification' in window) {
      permissionRef.current = Notification.permission === 'granted';
    }
  }, []);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (MiniKit.isInstalled()) {
      try {
        // Use MiniKit's permission system — shows World App's native prompt
        const result = await MiniKit.commandsAsync.requestPermission({
          permission: Permission.Notifications,
        });
        const granted = (result as any)?.status === 'success';
        permissionRef.current = granted;
        return granted;
      } catch {
        return false;
      }
    }

    // Fallback: standard browser Notification API (dev/browser context)
    if (!('Notification' in window)) return false;
    if (Notification.permission === 'granted') { permissionRef.current = true; return true; }
    const result = await Notification.requestPermission();
    permissionRef.current = result === 'granted';
    return result === 'granted';
  }, []);

  const notify = useCallback((title: string, body: string, onClick?: () => void) => {
    // Always play sound + vibrate for in-app feedback
    playNotificationSound();
    vibrateDevice();

    if (MiniKit.isInstalled()) {
      // In World App: foreground alerts are handled by sound/vibration.
      // Background push notifications are sent via the backend API.
      return;
    }

    // Fallback for browser context
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;
    if (document.visibilityState === 'visible') return; // Don't show if app is focused

    const n = new Notification(title, {
      body,
      icon: '/favicon.svg',
      badge: '/favicon.svg',
      tag: `joystance_${Date.now()}`,
    });

    if (onClick) {
      n.onclick = () => { window.focus(); onClick(); n.close(); };
    }
    setTimeout(() => n.close(), 5000);
  }, []);

  const isSupported = MiniKit.isInstalled() || 'Notification' in window;

  return { requestPermission, notify, isSupported };
}
